import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Grid,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  GetApp as TemplateIcon,
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useTheme } from "@mui/material";


interface UploadProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  percentage: number;
}

interface UploadError {
  row: number;
  error: string;
  registrationNumber?: string;
}

interface VehicleRecord {
  registrationNumber: string;
  hubName: string;
  modelName: string;
  oemName: string;
  color: string;
  year: number;
  chassisNumber?: string;
  motorNumber?: string;
  chargerNumber?: string;
  chargerType?: string;
  powerOutput?: number;
  batteryConfiguration?: string;
  battery1Number?: string;
  battery2Number?: string;
  batteryNumber?: string; // Added batteryNumber
  variant?: string;
  batteryCapacity?: number;
  maxRange?: number;
  maxSpeed?: number;
  purchaseDate?: string;
  purchasePrice?: number;
  currentValue?: number;
  mileage?: number;
  operationalStatus?: string;
  serviceStatus?: string;
  rcNumber?: string;
  rcValidUpto?: string;
  insuranceProvider?: string;
  policyNumber?: string;
  policyStartDate?: string;
  policyEndDate?: string;
  premiumAmount?: number;
  coverageAmount?: number;
  vehiclePhotoFront?: string;
  vehiclePhotoBack?: string;
  vehiclePhotoLeft?: string;
  vehiclePhotoRight?: string;
  rcDocument?: string;
  insuranceDocument?: string;
}

export const BulkVehicleUpload: React.FC = () => {
  const theme = useTheme();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [status, setStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle');
  const [errors, setErrors] = useState<UploadError[]>([]);
  const [preview, setPreview] = useState<VehicleRecord[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const isExcel = selectedFile.name.toLowerCase().endsWith('.xlsx') ||
                      selectedFile.name.toLowerCase().endsWith('.xls');

      if (!isExcel) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setErrors([]);
      setStatus('idle');
      setProgress(null);
      setPreview([]);
      setShowPreview(false);
    }
  };

  const previewExcelFile = async () => {
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json<any>(worksheet);

      // Map common spreadsheet column names to our VehicleRecord keys
      const mapped: VehicleRecord[] = raw.map((row: any) => ({
        registrationNumber:
          row['Registration Number'] || row['registrationNumber'] || row['Registration'] || row['registration'] || '',
        hubName: row['Hub Name'] || row['hubName'] || row['Hub'] || '',
        oemName: row['OEM Name'] || row['oemName'] || row['OEM'] || '',
        modelName: row['Model Name'] || row['modelName'] || row['Model'] || '',
        color: row['Color'] || row['color'] || '',
        year: Number(row['Year'] || row['year'] || 0) || 0,
        chassisNumber: row['Chassis Number'] || row['chassisNumber'] || '',
        motorNumber: row['Motor Number'] || row['motorNumber'] || '',
        chargerNumber: row['Charger Number'] || row['chargerNumber'] || '',
        chargerType: row['Charger Type'] || row['chargerType'] || '',
        powerOutput: row['Power Output (kW)'] || row['powerOutput'] || undefined,
        batteryConfiguration: row['Battery Configuration'] || row['batteryConfiguration'] || '',
        battery1Number: row['Battery 1 Number'] || row['Battery Number'] || row['battery1Number'] || row['batteryNumber'] || '',
        battery2Number: row['Battery 2 Number'] || row['battery2Number'] || '',
        batteryNumber: row['Battery Number'] || row['batteryNumber'] || '',
        variant: row['Variant'] || row['variant'] || '',
        batteryCapacity: row['Battery Capacity (kWh)'] || row['batteryCapacity'] || undefined,
        maxRange: row['Max Range (km)'] || row['maxRange'] || undefined,
        maxSpeed: row['Max Speed (km/h)'] || row['maxSpeed'] || undefined,
        purchaseDate: row['Purchase Date'] || row['purchaseDate'] || undefined,
        purchasePrice: row['Purchase Price'] || row['purchasePrice'] || undefined,
        currentValue: row['Current Value'] || row['currentValue'] || undefined,
        mileage: row['Mileage (km)'] || row['mileage'] || undefined,
        operationalStatus: row['Operational Status'] || row['operationalStatus'] || '',
        serviceStatus: row['Service Status'] || row['serviceStatus'] || '',
        rcNumber: row['RC Number'] || row['rcNumber'] || '',
        rcValidUpto: row['RC Valid Upto'] || row['rcValidUpto'] || '',
        insuranceProvider: row['Insurance Provider'] || row['insuranceProvider'] || '',
        policyNumber: row['Policy Number'] || row['policyNumber'] || '',
        policyStartDate: row['Policy Start Date'] || row['policyStartDate'] || undefined,
        policyEndDate: row['Policy End Date'] || row['policyEndDate'] || undefined,
        premiumAmount: row['Premium Amount'] || row['premiumAmount'] || undefined,
        coverageAmount: row['Coverage Amount'] || row['coverageAmount'] || undefined,
        vehiclePhotoFront: row['vehiclePhotoFront'] || row['Vehicle Photo Front'] || '',
        vehiclePhotoBack: row['vehiclePhotoBack'] || row['Vehicle Photo Back'] || '',
        vehiclePhotoLeft: row['vehiclePhotoLeft'] || row['Vehicle Photo Left'] || '',
        vehiclePhotoRight: row['vehiclePhotoRight'] || row['Vehicle Photo Right'] || '',
        rcDocument: row['rcDocument'] || row['RC Document'] || '',
        insuranceDocument: row['insuranceDocument'] || row['Insurance Document'] || '',
      }));

      setPreview(mapped.slice(0, 2000));
      setShowPreview(true);
    } catch (error) {
      console.error('Error reading Excel file:', error);
      alert('Error reading Excel file. Please ensure it is a valid .xlsx file.');
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        'Registration Number': 'MH02AB1234',
        'Hub Name': 'Mysore Hub',
        'OEM Name': 'Tata',
        'Model Name': 'Tiago EV',
        'Color': 'White',
        'Year': 2024,
        'Chassis Number': 'TMBDF65ASME123456',
        'Motor Number': 'MOT12345',
        'Charger Number': 'CHG12345',
        'Charger Type': 'Portable AC Single Phase',
        'Power Output (kW)': 3.3,
        'Battery Configuration': 'Single',
        'Battery 1 Number': 'BAT12345',
        'Battery Number': 'BAT12345', // Added battery number
        'Battery 2 Number': '',
        'Variant': 'XT',
        'Battery Capacity (kWh)': 30,
        'Max Range (km)': 315,
        'Max Speed (km/h)': 160,
        'Purchase Date': '2024-01-15',
        'Purchase Price': 750000,
        'Current Value': 700000,
        'Mileage (km)': 0,
        'Operational Status': 'Available',
        'Service Status': 'Active',
        'RC Number': 'DL01AB1234',
        'RC Valid Upto': '2029-01-15',
        'Insurance Provider': 'ICICI',
        'Policy Number': 'POL123456',
        'Policy Start Date': '2024-01-15',
        'Policy End Date': '2025-01-14',
        'Premium Amount': 15000,
        'Coverage Amount': 750000,
        // 🚀 NEW FIELDS FOR BULK DOCUMENT UPLOAD
      vehiclePhotoFront: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      vehiclePhotoBack: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      vehiclePhotoLeft: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      vehiclePhotoRight: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",

      rcDocument: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      insuranceDocument: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      },
      {
        'Registration Number': 'KA05CD6789',
        'Hub Name': 'Bangalore Hub',
        'OEM Name': 'Ather',
        'Model Name': '450X',
        'Color': 'Grey',
        'Year': 2024,
        'Chassis Number': 'ATHDF65ASME567890',
        'Motor Number': 'MOT67890',
        'Charger Number': 'CHG67890',
        'Charger Type': 'Portable AC Single Phase',
        'Power Output (kW)': 3.3,
        'Battery Configuration': 'Dual',
        'Battery 1 Number': 'BAT67890',
        'Battery Number': 'BAT67890', // Added battery number
        'Battery 2 Number': 'BAT67891',
        'Variant': 'Pro',
        'Battery Capacity (kWh)': 3.7,
        'Max Range (km)': 146,
        'Max Speed (km/h)': 90,
        'Purchase Date': '2024-02-10',
        'Purchase Price': 165000,
        'Current Value': 160000,
        'Mileage (km)': 0,
        'Operational Status': 'Available',
        'Service Status': 'Active',
        'RC Number': 'KA05CD6789',
        'RC Valid Upto': '2029-02-10',
        'Insurance Provider': 'HDFC',
        'Policy Number': 'POL654321',
        'Policy Start Date': '2024-02-10',
        'Policy End Date': '2025-02-09',
        'Premium Amount': 8000,
        'Coverage Amount': 165000,
        // 🚀 NEW FIELDS FOR BULK DOCUMENT UPLOAD
      vehiclePhotoFront: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      vehiclePhotoBack: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      vehiclePhotoLeft: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      vehiclePhotoRight: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",

      rcDocument: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      insuranceDocument: "https://drive.google.com/file/d/1ZTAQYH3B5XADBD7UI-_3KilAVFSVs5DA/view?usp=sharing",
      },
    ];

  const worksheet = XLSX.utils.json_to_sheet(templateData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Vehicles");
  XLSX.writeFile(workbook, "Vehicle_Import_Template.xlsx");
};

  const validateFile = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validateOnly', 'true');

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post('/api/vehicles/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.data.isValid) {
        alert(
          `✅ Validation passed!\n\n${response.data.totalRecords} records are ready to upload.`
        );
        setErrors([]);
        previewExcelFile();
      } else {
        setErrors(response.data.errors || []);
        alert(
          `❌ Validation failed!\n\n${response.data.errors?.length || 0} errors found.\n\nCheck the errors list below for details.`
        );
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Validation failed';
      alert(`❌ Validation Error:\n\n${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const startUpload = async () => {
    if (!file) return;

    if (!confirm('⚠️ This will import all vehicles from the Excel file.\n\nAre you sure you want to proceed?')) {
      return;
    }

    setUploading(true);
    setStatus('processing');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('validateOnly', 'false');
    formData.append('skipExisting', 'true');
    formData.append('batchSize', '50');

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post('/api/vehicles/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      pollUploadStatus(response.data.uploadId);
    } catch (error: any) {
      console.error('Upload error:', error);
      setStatus('failed');
      const errorMsg = error.response?.data?.error || error.message || 'Upload failed to start';
      alert(`❌ Upload Error:\n\n${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const pollUploadStatus = async (id: string) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const poll = setInterval(async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`/api/vehicles/bulk-upload/${id}/status`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        const data = response.data;

        setProgress(data.progress);
        setStatus(data.status);
        setErrors(data.errors || []);

        if (data.status === 'completed') {
          clearInterval(poll);
          pollIntervalRef.current = null;
          // Show user the preview of imported file after completion
          try {
            await previewExcelFile();
            setShowPreview(true);
          } catch (err) {
            console.warn('Unable to generate preview after upload:', err);
          }

          alert(
            `✅ Upload completed successfully!\n\n${data.progress.successful} vehicles imported.\n${data.progress.failed} failed.`
          );
        } else if (data.status === 'failed') {
          clearInterval(poll);
          pollIntervalRef.current = null;
          // Show preview as well so user can inspect rows and errors
          try {
            await previewExcelFile();
            setShowPreview(true);
          } catch (err) {
            console.warn('Unable to generate preview after failed upload:', err);
          }

          alert(`❌ Upload failed!\n\nError: ${data.error}`);
        }
      } catch (error) {
        console.error('Status polling error:', error);
        clearInterval(poll);
        pollIntervalRef.current = null;
      }
    }, 2000);

    pollIntervalRef.current = poll;
  };

  const downloadErrors = () => {
    const errorData = errors.map((err) => ({
      'Row': err.row,
      'Registration Number': err.registrationNumber || 'N/A',
      'Error': err.error,
    }));

    const worksheet = XLSX.utils.json_to_sheet(errorData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');
    XLSX.writeFile(workbook, `Vehicle_Upload_Errors_${new Date().getTime()}.xlsx`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Vehicle Bulk Import
      </Typography>

      <Grid container spacing={3}>
        {/* Upload Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Excel File
              </Typography>

              <Box
  sx={{
    border: "2px dashed",
    borderColor: theme.palette.primary.main,   // Green/Blue neon border
    borderRadius: 2,
    p: 3,
    textAlign: "center",
    cursor: "pointer",
    mb: 2,

    // MATCH DARK/LIGHT AUTOMATICALLY
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(11,32,63,0.75)"   // same as dashboard card
        : "#f4f7ff",              // soft clean white

    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(11,32,63,0.95)"  // subtle neon hover
          : "#e8f1ff",
      transform: "scale(.99)",
      transition: "0.2s",
    },
  }}
  onClick={() => fileInputRef.current?.click()}
>
  <UploadIcon
    sx={{
      fontSize: 48,
      color: theme.palette.secondary.main, // future electric blue
      mb: 1,
    }}
  />

  <Typography variant="body1">
    {file ? `✓ ${file.name}` : `Click to upload or drag and drop`}
  </Typography>

  <Typography variant="caption" color="text.secondary">
    Excel files only (.xlsx, .xls)
  </Typography>
</Box>


              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleFileSelect}
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<TemplateIcon />}
                  onClick={downloadTemplate}
                  fullWidth
                >
                  Download Template
                </Button>
                {file && (
                  <>
                    <Button
                      variant="contained"
                      onClick={validateFile}
                      disabled={uploading || status === 'processing'}
                      fullWidth
                    >
                      Validate
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={startUpload}
                      disabled={uploading || status === 'processing' || errors.length > 0}
                      fullWidth
                    >
                      Import
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Status
              </Typography>

              {status === 'idle' ? (
                <Alert severity="info">Select a file to begin</Alert>
              ) : status === 'processing' ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Processing... {progress?.percentage}%
                  </Alert>
                  <LinearProgress variant="determinate" value={progress?.percentage || 0} />
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption">Total</Typography>
                          <Typography variant="h6">{progress?.total}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center' }}>
                          <Typography variant="caption">Processed</Typography>
                          <Typography variant="h6">{progress?.processed}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: '#33c13eff' }}>
                          <Typography variant="caption">Successful</Typography>
                          <Typography variant="h6" color="success">{progress?.successful}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: '#0fa8e0ff' }}>
                          <Typography variant="caption">Failed</Typography>
                          <Typography variant="h6" color="error">{progress?.failed}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              ) : status === 'completed' ? (
                <Alert severity="success">
                  Upload completed! {progress?.successful} vehicles imported successfully.
                </Alert>
              ) : (
                <Alert severity="error">
                  Upload failed. Check the errors below.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Preview of Vehicles</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fd0000ff' }}>
                  <TableCell>Registration</TableCell>
                  <TableCell>Hub</TableCell>
                  <TableCell>OEM</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Battery Number</TableCell>
                  <TableCell>Charger Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.registrationNumber}</TableCell>
                    <TableCell>{record.hubName}</TableCell>
                    <TableCell>{record.oemName}</TableCell>
                    <TableCell>{record.modelName}</TableCell>
                    <TableCell>{record.color}</TableCell>
                    <TableCell>{record.year}</TableCell>
                   <TableCell>
                     {record.batteryNumber || record.battery1Number}
                   </TableCell>
                  <TableCell>{record.chargerNumber}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Errors Table */}
      {errors.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Errors ({errors.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadErrors}
                size="small"
              >
                Download Errors
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f53350ff' }}>
                    <TableCell>Row</TableCell>
                    <TableCell>Registration Number</TableCell>
                    <TableCell>Error Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errors.slice(0, 20).map((error, index) => (
                    <TableRow key={index} sx={{ backgroundColor: '#e89816ff' }}>
                      <TableCell>{error.row}</TableCell>
                      <TableCell>{error.registrationNumber || 'N/A'}</TableCell>
                      <TableCell>{error.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {errors.length > 20 && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Showing 20 of {errors.length} errors. Download the file to see all errors.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BulkVehicleUpload;